import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { API, useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, parse, isValid } from 'date-fns';
import MembershipModal from '@/components/MembershipModal';
import { Upload, X, Users, CalendarDays } from 'lucide-react';
import { useIsIOSApp } from '@/hooks/useIsIOSApp';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { COUNTRIES, EVENT_CATEGORIES, GROUP_TO_EVENT_CATEGORY } from '@/constants';
import EventTemplatePicker from '@/components/EventTemplatePicker';

const CreateEvent = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isIOSApp = useIsIOSApp();
  const [loading, setLoading] = useState(false);
  const [showMembership, setShowMembership] = useState(false);
  const [myGroups, setMyGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  // Set when this CreateEvent flow was launched from a Poll → Convert to Event.
  // Carries { from_poll_id, suggested_invitees }.
  const [pollPrefill, setPollPrefill] = useState(null);
  const [autoRsvpVoters, setAutoRsvpVoters] = useState(true);
  const fileInputRef = useRef(null);
  
  const DRAFT_KEY = 'expatssphere_event_draft';

  const [formData, setFormData] = useState(() => {
    // Load draft from localStorage if available
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const draft = JSON.parse(saved);
        return { ...{
          title: '', description: '', category: '', country: '', city: '',
          location_details: '', map_link: '', ticket_url: '', event_date: '',
          event_time: '', max_attendees: '', image_url: '', group_slug: '',
          is_invite_only: false, is_recurring: false, recurrence_type: '',
          recurrence_end_type: '', recurrence_count: '', recurrence_end_date: '',
          is_shared_listing: false, external_host_name: '', external_source_url: '',
      auto_reminder_24h: false,
          allow_guests: false, max_guests_per_rsvp: '1',
        }, ...draft.formData };
      }
    } catch {}
    return {
      title: '', description: '', category: '', country: '', city: '',
      location_details: '', map_link: '', ticket_url: '', event_date: '',
      event_time: '', max_attendees: '', image_url: '', group_slug: '',
      is_invite_only: false, is_recurring: false, recurrence_type: '',
      recurrence_end_type: '', recurrence_count: '', recurrence_end_date: '',
      is_shared_listing: false, external_host_name: '', external_source_url: '',
      auto_reminder_24h: false,
      allow_guests: false, max_guests_per_rsvp: '1',
    };
  });

  const [hasDraft, setHasDraft] = useState(() => !!localStorage.getItem('expatssphere_event_draft'));

  useEffect(() => {
    // Fetch user's groups
    fetchMyGroups();
    // Restore selected groups from draft
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const draft = JSON.parse(saved);
        if (draft.selectedGroups) setSelectedGroups(draft.selectedGroups);
        if (draft.imageFile) setImageFile(draft.imageFile);
      }
    } catch {}
    // Hydrate from a poll-prefill (when arriving from a Poll → Convert to Event)
    try {
      const raw = sessionStorage.getItem('expatssphere_poll_prefill');
      if (raw) {
        const prefill = JSON.parse(raw);
        setFormData((prev) => ({
          ...prev,
          title: prefill.title || prev.title,
          description: prefill.description || prev.description,
          city: prefill.city || prev.city,
          country: prefill.country || prev.country,
          event_date: prefill.event_date || prev.event_date,
          event_time: prefill.event_time || prev.event_time,
        }));
        if (prefill.image_url) {
          setImageFile(prefill.image_url);
          setImagePreview(prefill.image_url);
        }
        if (prefill.from_poll_id) {
          setPollPrefill({
            from_poll_id: prefill.from_poll_id,
            suggested_invitees: prefill.suggested_invitees || [],
          });
        }
        toast.success('Pre-filled from your poll. Add a date and you\'re ready!');
        sessionStorage.removeItem('expatssphere_poll_prefill');
      }
    } catch {}
  }, [user]);

  const saveDraft = () => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        formData,
        selectedGroups,
        imageFile,
        savedAt: new Date().toISOString()
      }));
      setHasDraft(true);
      toast.success('Draft saved!');
    } catch {
      toast.error('Failed to save draft');
    }
  };

  const discardDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setHasDraft(false);
    toast.success('Draft discarded');
  };

  const fetchMyGroups = async () => {
    try {
      const response = await axios.get(`${API}/users/me/groups`);
      setMyGroups(response.data || []);
    } catch (error) {
      console.log('Could not fetch groups');
    }
  };

  const handleGroupToggle = (groupSlug) => {
    setSelectedGroups(prev => {
      const updated = prev.includes(groupSlug)
        ? prev.filter(s => s !== groupSlug)
        : [...prev, groupSlug];
      setFormData(f => ({ ...f, group_slugs: updated, group_slug: updated[0] || null }));
      return updated;
    });
  };

  const handleSelectAllGroups = () => {
    if (selectedGroups.length === myGroups.length) {
      setSelectedGroups([]);
      setFormData(f => ({ ...f, group_slugs: [], group_slug: null }));
    } else {
      const allSlugs = myGroups.map(g => g.slug);
      setSelectedGroups(allSlugs);
      setFormData(f => ({ ...f, group_slugs: allSlugs, group_slug: allSlugs[0] || null }));
    }
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file');
        return;
      }
      // Auto-compress: resize to max 1200px and convert to JPEG
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        const maxDim = 1200;
        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round(height * (maxDim / width));
            width = maxDim;
          } else {
            width = Math.round(width * (maxDim / height));
            height = maxDim;
          }
        }
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        const compressed = canvas.toDataURL('image/jpeg', 0.85);
        setImageFile(compressed);
        setImagePreview(compressed);
        setFormData(prev => ({ ...prev, image_url: '' }));
      };
      img.onerror = () => toast.error('Failed to process image');
      img.src = URL.createObjectURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const applyTemplate = (tplBody) => {
    if (!tplBody) return;
    // Merge template fields into the form, keeping any date/time the user
    // already typed. Templates never carry date/time (server-side allowlist).
    setFormData((prev) => ({
      ...prev,
      ...Object.fromEntries(
        Object.entries(tplBody).filter(([, v]) => v !== null && v !== undefined)
      ),
      // Force normalized types matching form expectations
      max_attendees: tplBody.max_attendees != null ? String(tplBody.max_attendees) : prev.max_attendees,
      max_guests_per_rsvp: tplBody.max_guests_per_rsvp != null ? String(tplBody.max_guests_per_rsvp) : prev.max_guests_per_rsvp,
      recurrence_count: tplBody.recurrence_count != null ? String(tplBody.recurrence_count) : prev.recurrence_count,
      // Preserve user-entered date/time
      event_date: prev.event_date,
      event_time: prev.event_time,
    }));
    if (tplBody.image_url) {
      setImagePreview(tplBody.image_url);
      setImageFile(null);
    }
    if (tplBody.group_slug) {
      setSelectedGroups([tplBody.group_slug]);
    } else if (Array.isArray(tplBody.group_slugs) && tplBody.group_slugs.length) {
      setSelectedGroups(tplBody.group_slugs);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedGroups.length === 0) {
      toast.error('Please select at least one group for your event');
      return;
    }
    
    setLoading(true);

    try {
      // Auto-set category from first selected group or default to 'Other'
      const firstGroup = myGroups.find(g => g.slug === selectedGroups[0]);
      const autoCategory = firstGroup 
        ? (GROUP_TO_EVENT_CATEGORY[firstGroup.category] || 'Other')
        : 'Other';
      
      const data = {
        ...formData,
        group_slug: selectedGroups[0],
        group_slugs: selectedGroups,
        category: autoCategory,
        max_attendees: formData.max_attendees ? parseInt(formData.max_attendees) : null,
        // Use uploaded image if available, otherwise use URL
        image_url: imageFile || formData.image_url || '',
        // Clean up optional fields - convert empty strings to null for optional int/str fields
        group_slug: formData.group_slug || null,
        recurrence_type: formData.recurrence_type || null,
        recurrence_end_type: formData.recurrence_end_type || null,
        recurrence_count: formData.recurrence_count ? parseInt(formData.recurrence_count) : null,
        recurrence_end_date: formData.recurrence_end_date || null,
        from_poll_id: (pollPrefill && autoRsvpVoters) ? pollPrefill.from_poll_id : null,
        // Shared-listing normalization: only ship attribution fields when
        // the toggle is on, and clear them (null) when it's off so a user
        // toggling off doesn't accidentally send stale text.
        is_shared_listing: !!formData.is_shared_listing,
        external_host_name: formData.is_shared_listing ? (formData.external_host_name || '').trim() || null : null,
        external_source_url: formData.is_shared_listing ? (formData.external_source_url || '').trim() || null : null,
        auto_reminder_24h: !!formData.auto_reminder_24h,
        // Guest RSVPs — only ship when host opts in, and normalize "unlimited"
        // to null so the backend interprets it correctly.
        allow_guests: !!formData.allow_guests,
        max_guests_per_rsvp: !formData.allow_guests
          ? null
          : (formData.max_guests_per_rsvp === 'unlimited' || formData.max_guests_per_rsvp === '' || formData.max_guests_per_rsvp == null)
            ? null
            : parseInt(formData.max_guests_per_rsvp),
      };
      const response = await axios.post(`${API}/events`, data);
      localStorage.removeItem(DRAFT_KEY);
      const autoCount = pollPrefill && autoRsvpVoters ? (pollPrefill.suggested_invitees?.length || 0) : 0;
      if (autoCount > 0) {
        toast.success(`Event created — ${autoCount} poll voter${autoCount === 1 ? '' : 's'} auto-RSVPed!`);
      } else {
        toast.success('Event created successfully!');
      }
      navigate(`/events/${response.data.event_id}`);
    } catch (error) {
      if (error.response?.status === 403) {
        setShowMembership(true);
      } else {
        // Handle Pydantic validation errors (422) which return an array
        const detail = error.response?.data?.detail;
        let errorMessage = 'Failed to create event';
        
        if (Array.isArray(detail)) {
          // Extract the first error message from Pydantic validation errors
          errorMessage = detail[0]?.msg || detail[0]?.message || errorMessage;
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object' && detail?.msg) {
          errorMessage = detail.msg;
        }
        
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="mb-8">
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">Create Event</h1>
              {hasDraft && formData.title && (
                <div className="flex items-center gap-3 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-sm text-amber-800 flex-1">Draft restored: <strong>{formData.title}</strong></p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={discardDraft}
                    className="text-amber-700 hover:text-red-600 text-xs"
                    data-testid="discard-draft-button"
                  >
                    Discard
                  </Button>
                </div>
              )}
              {pollPrefill && (
                <div
                  className="flex items-start gap-3 mb-4 p-4 bg-violet-50 border-2 border-violet-200 rounded-xl"
                  data-testid="poll-conversion-banner"
                >
                  <span className="text-2xl leading-none mt-0.5">🎉</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-violet-900">
                      Converting your poll into an event
                    </p>
                    <p className="text-xs text-violet-800 mt-0.5">
                      {pollPrefill.suggested_invitees.length > 0
                        ? `${pollPrefill.suggested_invitees.length} ${pollPrefill.suggested_invitees.length === 1 ? 'voter' : 'voters'} from the winning option will be auto-RSVPed and notified.`
                        : 'No voters yet — share the event once it\'s live.'}
                    </p>
                    {pollPrefill.suggested_invitees.length > 0 && (
                      <label className="inline-flex items-center gap-2 mt-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={autoRsvpVoters}
                          onChange={(e) => setAutoRsvpVoters(e.target.checked)}
                          className="rounded border-violet-300 text-violet-600"
                          data-testid="auto-rsvp-voters-toggle"
                        />
                        <span className="text-xs font-medium text-violet-900">
                          Auto-RSVP {pollPrefill.suggested_invitees.length} {pollPrefill.suggested_invitees.length === 1 ? 'voter' : 'voters'} and send them a push
                        </span>
                      </label>
                    )}
                  </div>
                </div>
              )}
              <p className="text-lg text-muted-foreground">
                Host a meetup, gathering, or event for your community
              </p>
            </div>

            <div className="bg-white rounded-3xl p-8 md:p-12 border-2 border-border brutal-shadow">
              <EventTemplatePicker onPick={applyTemplate} />
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="title">Event Title *</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="h-12 rounded-xl border-2"
                    placeholder="e.g., Coffee & Chat for New Expats"
                    data-testid="create-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    className="rounded-xl border-2 min-h-[180px]"
                    placeholder="Describe your event...

You can use:
• Separate paragraphs by pressing Enter twice
• Bullet points by starting a line with - or •

Example:
Join us for a fun evening!

Schedule:
- 6:00 PM: Welcome drinks
- 7:00 PM: Main activity
- 9:00 PM: Networking"
                    data-testid="create-description-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Tip: Press Enter for new paragraphs. Use - or • at the start of a line for bullet points.
                  </p>
                </div>

                {/* Group Selector - Multi-select */}
                <div className="space-y-2">
                  <Label htmlFor="group" className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Group(s) *
                  </Label>
                  {myGroups.length > 0 ? (
                    <div className="border-2 rounded-xl p-3 space-y-2 max-h-48 overflow-y-auto">
                      <label className="flex items-center gap-2 cursor-pointer pb-2 border-b">
                        <input
                          type="checkbox"
                          checked={selectedGroups.length === myGroups.length}
                          onChange={handleSelectAllGroups}
                          className="rounded border-gray-300 text-primary focus:ring-primary"
                          data-testid="select-all-groups"
                        />
                        <span className="text-sm font-semibold">All Groups</span>
                      </label>
                      {myGroups.map((group) => (
                        <label key={group.slug} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedGroups.includes(group.slug)}
                            onChange={() => handleGroupToggle(group.slug)}
                            className="rounded border-gray-300 text-primary focus:ring-primary"
                            data-testid={`group-checkbox-${group.slug}`}
                          />
                          <span className="text-sm">{group.name}</span>
                          <span className="text-xs text-muted-foreground">({group.category})</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground bg-gray-50 rounded-xl p-3 border">
                      You need to join or create a group before creating an event.
                    </p>
                  )}
                    {selectedGroups.length > 0 && (
                      <p className="text-xs text-primary">
                        Members of {selectedGroups.length} group{selectedGroups.length > 1 ? 's' : ''} will be notified about this event!
                      </p>
                    )}
                  </div>

                {/* Category auto-set from group or defaults to 'Other' - no manual selection needed */}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="country">Country *</Label>
                    <SearchableSelect
                      options={COUNTRIES}
                      value={formData.country}
                      onValueChange={(value) => setFormData({ ...formData, country: value })}
                      placeholder="Select Country"
                      searchPlaceholder="Type to search country..."
                      data-testid="create-country-select"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="h-12 rounded-xl border-2"
                      placeholder="e.g., Bangkok"
                      data-testid="create-city-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location_details">Location *</Label>
                  <Input
                    id="location_details"
                    name="location_details"
                    value={formData.location_details}
                    onChange={handleChange}
                    required
                    className="h-12 rounded-xl border-2"
                    placeholder="e.g., Starbucks Sukhumvit, Ground Floor"
                    data-testid="create-location-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="map_link">Map Link (Google Maps / Apple Maps)</Label>
                  <Input
                    id="map_link"
                    name="map_link"
                    value={formData.map_link}
                    onChange={handleChange}
                    className="h-12 rounded-xl border-2"
                    placeholder="e.g., https://maps.google.com/..."
                    data-testid="create-map-link-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Add a Google Maps or Apple Maps link so attendees can easily find the venue
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="event_date">Date *</Label>
                    {/* Shadcn Popover+Calendar — reliable in Android TWA /
                        iOS PWA where the native <input type="date"> can get
                        stuck in segment-edit mode. */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="h-12 w-full justify-start rounded-xl border-2 font-normal"
                          data-testid="create-date-input"
                        >
                          <CalendarDays className="w-4 h-4 mr-2 text-muted-foreground" />
                          {formData.event_date
                            ? (() => {
                                const parsed = parse(formData.event_date, 'yyyy-MM-dd', new Date());
                                return isValid(parsed) ? format(parsed, 'MMM d, yyyy') : formData.event_date;
                              })()
                            : <span className="text-muted-foreground">Pick a date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={(() => {
                            if (!formData.event_date) return undefined;
                            const parsed = parse(formData.event_date, 'yyyy-MM-dd', new Date());
                            return isValid(parsed) ? parsed : undefined;
                          })()}
                          onSelect={(d) => {
                            if (!d) return;
                            setFormData((prev) => ({ ...prev, event_date: format(d, 'yyyy-MM-dd') }));
                          }}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="event_time">Time *</Label>
                    <Select
                      value={formData.event_time}
                      onValueChange={(value) => setFormData({ ...formData, event_time: value })}
                      required
                    >
                      <SelectTrigger className="h-12 rounded-xl border-2" data-testid="create-time-input">
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        <SelectItem value="06:00">06:00</SelectItem>
                        <SelectItem value="06:30">06:30</SelectItem>
                        <SelectItem value="07:00">07:00</SelectItem>
                        <SelectItem value="07:30">07:30</SelectItem>
                        <SelectItem value="08:00">08:00</SelectItem>
                        <SelectItem value="08:30">08:30</SelectItem>
                        <SelectItem value="09:00">09:00</SelectItem>
                        <SelectItem value="09:30">09:30</SelectItem>
                        <SelectItem value="10:00">10:00</SelectItem>
                        <SelectItem value="10:30">10:30</SelectItem>
                        <SelectItem value="11:00">11:00</SelectItem>
                        <SelectItem value="11:30">11:30</SelectItem>
                        <SelectItem value="12:00">12:00</SelectItem>
                        <SelectItem value="12:30">12:30</SelectItem>
                        <SelectItem value="13:00">13:00</SelectItem>
                        <SelectItem value="13:30">13:30</SelectItem>
                        <SelectItem value="14:00">14:00</SelectItem>
                        <SelectItem value="14:30">14:30</SelectItem>
                        <SelectItem value="15:00">15:00</SelectItem>
                        <SelectItem value="15:30">15:30</SelectItem>
                        <SelectItem value="16:00">16:00</SelectItem>
                        <SelectItem value="16:30">16:30</SelectItem>
                        <SelectItem value="17:00">17:00</SelectItem>
                        <SelectItem value="17:30">17:30</SelectItem>
                        <SelectItem value="18:00">18:00</SelectItem>
                        <SelectItem value="18:30">18:30</SelectItem>
                        <SelectItem value="19:00">19:00</SelectItem>
                        <SelectItem value="19:30">19:30</SelectItem>
                        <SelectItem value="20:00">20:00</SelectItem>
                        <SelectItem value="20:30">20:30</SelectItem>
                        <SelectItem value="21:00">21:00</SelectItem>
                        <SelectItem value="21:30">21:30</SelectItem>
                        <SelectItem value="22:00">22:00</SelectItem>
                        <SelectItem value="22:30">22:30</SelectItem>
                        <SelectItem value="23:00">23:00</SelectItem>
                        <SelectItem value="23:30">23:30</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max_attendees">Max Attendees (Optional)</Label>
                  <Input
                    id="max_attendees"
                    name="max_attendees"
                    type="number"
                    value={formData.max_attendees}
                    onChange={handleChange}
                    className="h-12 rounded-xl border-2"
                    placeholder="Leave empty for unlimited"
                    data-testid="create-max-attendees-input"
                  />
                </div>

                {/* Recurrence Options */}
                <div className="space-y-4 p-4 bg-muted/50 rounded-xl border-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base font-medium">Recurring Event</Label>
                      <p className="text-sm text-muted-foreground">Repeat this event on a schedule</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={formData.is_recurring}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        is_recurring: e.target.checked,
                        recurrence_type: '',
                        recurrence_end_type: '',
                        recurrence_count: '',
                        recurrence_end_date: ''
                      })}
                      className="w-5 h-5 rounded border-2 accent-primary"
                      data-testid="recurring-checkbox"
                    />
                  </div>
                  
                  {formData.is_recurring && (
                    <div className="space-y-4 pt-2">
                      <div className="space-y-2">
                        <Label>Repeat</Label>
                        <Select
                          value={formData.recurrence_type}
                          onValueChange={(value) => setFormData({ ...formData, recurrence_type: value })}
                        >
                          <SelectTrigger className="h-12 rounded-xl border-2">
                            <SelectValue placeholder="Select frequency" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>End</Label>
                        <Select
                          value={formData.recurrence_end_type}
                          onValueChange={(value) => setFormData({ 
                            ...formData, 
                            recurrence_end_type: value,
                            recurrence_count: '',
                            recurrence_end_date: ''
                          })}
                        >
                          <SelectTrigger className="h-12 rounded-xl border-2">
                            <SelectValue placeholder="When to stop" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="occurrences">After X occurrences</SelectItem>
                            <SelectItem value="date">On a specific date</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {formData.recurrence_end_type === 'occurrences' && (
                        <div className="space-y-2">
                          <Label>Number of occurrences</Label>
                          <Input
                            type="number"
                            min="2"
                            max="52"
                            value={formData.recurrence_count}
                            onChange={(e) => setFormData({ ...formData, recurrence_count: e.target.value })}
                            className="h-12 rounded-xl border-2"
                            placeholder="e.g., 10"
                            data-testid="recurrence-count-input"
                          />
                        </div>
                      )}
                      
                      {formData.recurrence_end_type === 'date' && (
                        <div className="space-y-2">
                          <Label>End date</Label>
                          <Input
                            type="date"
                            value={formData.recurrence_end_date}
                            onChange={(e) => setFormData({ ...formData, recurrence_end_date: e.target.value })}
                            min={formData.event_date}
                            className="h-12 rounded-xl border-2"
                            data-testid="recurrence-end-date-input"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Invite Only Toggle */}
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl border-2">
                  <div>
                    <Label htmlFor="is_invite_only" className="font-semibold">Invite Only Event</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      People must request to join. You approve who attends.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={formData.is_invite_only}
                    onClick={() => setFormData(prev => ({ ...prev, is_invite_only: !prev.is_invite_only }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.is_invite_only ? 'bg-primary' : 'bg-gray-300'
                    }`}
                    data-testid="invite-only-toggle"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.is_invite_only ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Allow Guests Toggle (+1 RSVPs) */}
                <div className="p-4 bg-sky-50/60 rounded-xl border-2 border-sky-200" data-testid="allow-guests-section">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="allow_guests" className="font-semibold">Allow attendees to bring +1 guests</Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Members can bring guests (partners, friends). Guests count toward capacity.
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={formData.allow_guests}
                      onClick={() => setFormData(prev => ({ ...prev, allow_guests: !prev.allow_guests }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        formData.allow_guests ? 'bg-primary' : 'bg-gray-300'
                      }`}
                      data-testid="allow-guests-toggle"
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          formData.allow_guests ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  {formData.allow_guests && (
                    <div className="mt-3">
                      <Label htmlFor="max_guests_per_rsvp" className="text-sm">Max guests per member</Label>
                      <select
                        id="max_guests_per_rsvp"
                        name="max_guests_per_rsvp"
                        value={formData.max_guests_per_rsvp}
                        onChange={(e) => setFormData(prev => ({ ...prev, max_guests_per_rsvp: e.target.value }))}
                        className="mt-1 w-full rounded-lg border-2 border-sky-200 px-3 py-2 text-sm bg-white focus:border-sky-400 focus:outline-none"
                        data-testid="max-guests-select"
                      >
                        <option value="1">1 guest (classic +1)</option>
                        <option value="2">2 guests</option>
                        <option value="5">5 guests</option>
                        <option value="unlimited">Unlimited</option>
                      </select>
                    </div>
                  )}
                </div>

                {/* Shared Listing Toggle */}
                <div className="p-4 bg-amber-50/60 rounded-xl border-2 border-amber-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="is_shared_listing" className="font-semibold">I'm not the host — I'm sharing this event</Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Broadcast an event you spotted so other expats can discover it. Attendees will see it's a community pick, not one you're organizing.
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={formData.is_shared_listing}
                      onClick={() => setFormData(prev => ({ ...prev, is_shared_listing: !prev.is_shared_listing }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ml-3 flex-shrink-0 ${
                        formData.is_shared_listing ? 'bg-amber-500' : 'bg-gray-300'
                      }`}
                      data-testid="shared-listing-toggle"
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          formData.is_shared_listing ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  {formData.is_shared_listing && (
                    <div className="mt-4 space-y-3">
                      <div className="space-y-1">
                        <Label htmlFor="external_host_name">Host / organizer name (optional)</Label>
                        <Input
                          id="external_host_name"
                          name="external_host_name"
                          type="text"
                          value={formData.external_host_name}
                          onChange={handleChange}
                          placeholder="e.g. Budapest Symphony Orchestra"
                          className="h-11 rounded-xl border-2 bg-white"
                          data-testid="external-host-name-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="external_source_url">Source / booking link (optional)</Label>
                        <Input
                          id="external_source_url"
                          name="external_source_url"
                          type="url"
                          value={formData.external_source_url}
                          onChange={handleChange}
                          placeholder="https://…"
                          className="h-11 rounded-xl border-2 bg-white"
                          data-testid="external-source-url-input"
                        />
                        <p className="text-xs text-muted-foreground">Where the real event lives — ticket page, museum site, Facebook event, etc.</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Auto 24h Reminder Toggle */}
                <div className="flex items-center justify-between p-4 bg-sky-50 rounded-xl border-2 border-sky-200">
                  <div>
                    <Label htmlFor="auto_reminder_24h" className="font-semibold">
                      Auto-reminder 24h before
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      If seats are still open, we'll nudge nearby members who haven't RSVP'd. One reminder per event, max.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={formData.auto_reminder_24h}
                    onClick={() => setFormData(prev => ({ ...prev, auto_reminder_24h: !prev.auto_reminder_24h }))}
                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                      formData.auto_reminder_24h ? 'bg-sky-500' : 'bg-gray-300'
                    }`}
                    data-testid="auto-reminder-toggle"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.auto_reminder_24h ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ticket_url">Ticket Purchase Link (Optional)</Label>
                  <Input
                    id="ticket_url"
                    name="ticket_url"
                    type="url"
                    value={formData.ticket_url}
                    onChange={handleChange}
                    className="h-12 rounded-xl border-2"
                    placeholder="https://eventbrite.com/your-event or any ticket link"
                    data-testid="create-ticket-url-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Add a link where attendees can purchase tickets (Eventbrite, Ticketmaster, etc.)
                  </p>
                </div>

                {/* Event Image - Upload or URL */}
                <div className="space-y-4">
                  <Label>Event Image (Optional)</Label>
                  
                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="relative">
                      <img 
                        src={imagePreview} 
                        alt="Event preview" 
                        className="w-full h-48 object-cover rounded-xl border-2"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2 rounded-full"
                        onClick={removeImage}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  {!imagePreview && (
                    <div 
                      className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm font-medium">Click to upload image</p>
                      <p className="text-xs text-muted-foreground">or drag and drop • Max 2MB</p>
                    </div>
                  )}
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={isIOSApp ? ".jpg,.jpeg,.png,.gif,.webp,.heic" : "image/*"}
                    onChange={handleImageFileChange}
                    className="hidden"
                    data-testid="create-image-upload"
                  />

                  {/* Or use URL */}
                  {!imagePreview && (
                    <div className="space-y-2">
                      <Label htmlFor="image_url" className="text-sm text-muted-foreground">
                        Or paste an image URL
                      </Label>
                      <Input
                        id="image_url"
                        name="image_url"
                        type="url"
                        value={formData.image_url}
                        onChange={handleChange}
                        className="h-12 rounded-xl border-2"
                        placeholder="https://images.unsplash.com/..."
                        data-testid="create-image-input"
                      />
                    </div>
                  )}
                </div>

                <div className="flex gap-4 pt-6">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 h-12 rounded-full font-bold brutal-shadow"
                    data-testid="create-submit-button"
                  >
                    {loading ? 'Creating...' : 'Create Event'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={saveDraft}
                    className="h-12 px-6 rounded-full font-bold border-2"
                    data-testid="save-draft-button"
                  >
                    Save Draft
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/events')}
                    className="h-12 px-6 rounded-full font-bold border-2"
                    data-testid="create-cancel-button"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>

      {showMembership && <MembershipModal open={showMembership} onClose={() => setShowMembership(false)} />}
    </div>
  );
};

export default CreateEvent;